/**
 * Rego language knowledge for the policy editor: keywords, the policy skeleton, and the
 * builtin functions worth reaching for when gating infrastructure artefacts.
 *
 * This is deliberately a curated list, not a dump of OPA's ~200 builtins - the editor is a
 * small box in a modal, and an author writing a Terramantle gate wants string/collection
 * helpers, version and network comparisons, and the null guards the trust-signal policies
 * need. Everything here parses under OPA 1.x (Rego v1), which is what the scanners run, so
 * nothing removed in v1 (`any`, `all`, `re_match`, `cast_*`) is offered.
 *
 * Schema-driven `input.*` completion lives in regoSchema.ts; this module is the part that
 * does not depend on which artefact is being gated.
 */

export type CompletionFlavour = 'keyword' | 'function' | 'snippet' | 'variable'

export interface RegoCompletion {
  label: string
  /** Body to insert; carries ${n:…} placeholders when `snippet` is true. */
  insertText: string
  snippet: boolean
  flavour: CompletionFlavour
  detail?: string
  documentation?: string
  /** What Monaco filters the typed word against (differs from the label for `ns.member`). */
  filterText?: string
  /** Sort bucket: the skeleton first, then keywords, then builtins. */
  group: number
}

// ── Builtin functions ─────────────────────────────────────────────────────────

interface Builtin {
  name: string
  /** Snippet placeholders for the call arguments, e.g. `${1:haystack}, ${2:needle}`. */
  args: string
  doc: string
}

const BUILTINS: Builtin[] = [
  // Strings
  { name: 'sprintf',    args: '"${1:%s}", [${2:values}]', doc: 'Format a string - how deny messages are built.' },
  { name: 'concat',     args: '"${1:, }", ${2:collection}', doc: 'Join a collection with a delimiter.' },
  { name: 'contains',   args: '${1:haystack}, ${2:needle}', doc: 'True when the string contains the substring.' },
  { name: 'startswith', args: '${1:string}, ${2:prefix}', doc: 'True when the string has the prefix.' },
  { name: 'endswith',   args: '${1:string}, ${2:suffix}', doc: 'True when the string has the suffix.' },
  { name: 'lower',      args: '${1:string}', doc: 'Lowercase the string.' },
  { name: 'upper',      args: '${1:string}', doc: 'Uppercase the string.' },
  { name: 'split',      args: '${1:string}, "${2:/}"', doc: 'Split the string into an array on a delimiter.' },
  { name: 'replace',    args: '${1:string}, "${2:old}", "${3:new}"', doc: 'Replace every occurrence of a substring.' },
  { name: 'trim_space', args: '${1:string}', doc: 'Strip leading and trailing whitespace.' },
  { name: 'substring',  args: '${1:string}, ${2:0}, ${3:-1}', doc: 'Slice the string (length -1 reads to the end).' },
  { name: 'indexof',    args: '${1:string}, ${2:search}', doc: 'Index of the first occurrence, or -1.' },
  { name: 'to_number',  args: '${1:value}', doc: 'Parse a string as a number.' },
  { name: 'format_int', args: '${1:number}, ${2:10}', doc: 'Render a number in the given base.' },

  // Collections
  { name: 'count', args: '${1:collection}', doc: 'Number of elements - `count(input.findings) > 10`.' },
  { name: 'sum',   args: '${1:collection}', doc: 'Sum of a collection of numbers.' },
  { name: 'max',   args: '${1:collection}', doc: 'Largest element of a collection.' },
  { name: 'min',   args: '${1:collection}', doc: 'Smallest element of a collection.' },
  { name: 'sort',  args: '${1:collection}', doc: 'Ascending sort, returned as an array.' },
  { name: 'walk',  args: '${1:value}, [${2:path}, ${3:value}]', doc: 'Recursively walk a document, binding each path/value.' },
  { name: 'object.get',   args: '${1:object}, "${2:key}", ${3:null}', doc: 'Read a key with a fallback - the safe way to touch optional fields.' },
  { name: 'object.keys',  args: '${1:object}', doc: 'Set of the object’s keys.' },
  { name: 'object.union', args: '${1:a}, ${2:b}', doc: 'Merge two objects (the second wins).' },

  // Types and null guards
  { name: 'is_null',    args: '${1:value}', doc: 'True when the value is null - trust signals are null when undiscoverable.' },
  { name: 'is_string',  args: '${1:value}', doc: 'True when the value is a string.' },
  { name: 'is_number',  args: '${1:value}', doc: 'True when the value is a number.' },
  { name: 'is_boolean', args: '${1:value}', doc: 'True when the value is a boolean.' },
  { name: 'is_array',   args: '${1:value}', doc: 'True when the value is an array.' },
  { name: 'is_object',  args: '${1:value}', doc: 'True when the value is an object.' },
  { name: 'type_name',  args: '${1:value}', doc: 'Name of the value’s type, e.g. "string".' },

  // Numbers
  { name: 'abs',   args: '${1:number}', doc: 'Absolute value.' },
  { name: 'round', args: '${1:number}', doc: 'Round to the nearest integer.' },
  { name: 'ceil',  args: '${1:number}', doc: 'Round up.' },
  { name: 'floor', args: '${1:number}', doc: 'Round down.' },

  // Pattern matching
  { name: 'regex.match',   args: '"${1:pattern}", ${2:value}', doc: 'True when the value matches the regular expression.' },
  { name: 'regex.replace', args: '${1:value}, "${2:pattern}", "${3:replacement}"', doc: 'Replace every regex match.' },
  { name: 'glob.match',    args: '"${1:pattern}", ["${2:/}"], ${3:value}', doc: 'Glob match, e.g. `glob.match("aws_*", [], name)`.' },

  // Versions and networks - the comparisons infrastructure gates actually need
  { name: 'semver.compare',  args: '${1:a}, ${2:b}', doc: 'Compare two semvers: -1, 0 or 1. Gate on minimum versions.' },
  { name: 'semver.is_valid', args: '${1:value}', doc: 'True when the string is a valid semver.' },
  { name: 'net.cidr_contains',   args: '"${1:0.0.0.0/0}", ${2:value}', doc: 'True when the CIDR contains the address or CIDR.' },
  { name: 'net.cidr_intersects', args: '"${1:10.0.0.0/8}", ${2:value}', doc: 'True when two CIDRs overlap.' },

  // Time
  { name: 'time.now_ns',            args: '', doc: 'Current wall-clock time in nanoseconds.' },
  { name: 'time.parse_rfc3339_ns',  args: '${1:value}', doc: 'Parse an RFC3339 timestamp to nanoseconds.' },
  { name: 'time.add_date',          args: '${1:ns}, ${2:0}, ${3:-6}, ${4:0}', doc: 'Shift a timestamp by years/months/days - age checks.' },

  // Encoding
  { name: 'json.marshal',   args: '${1:value}', doc: 'Serialise a value to a JSON string.' },
  { name: 'json.unmarshal', args: '${1:string}', doc: 'Parse a JSON string.' },
]

/** `sprintf` + `"${1:%s}", [${2:values}]` -> `sprintf("%s", [values])` for the detail line. */
export function signature(name: string, args: string): string {
  const rendered = args.replace(/\$\{\d+:([^}]*)\}/g, '$1').replace(/\$\{\d+\}/g, '')
  return `${name}(${rendered})`
}

const lastSegment = (name: string) => name.slice(name.lastIndexOf('.') + 1)

function builtinCompletion(builtin: Builtin, insertName: string): RegoCompletion {
  return {
    label:         builtin.name,
    insertText:    `${insertName}(${builtin.args})`,
    snippet:       true,
    flavour:       'function',
    detail:        signature(builtin.name, builtin.args),
    documentation: builtin.doc,
    filterText:    insertName,
    group:         2,
  }
}

// ── Keywords and the policy skeleton ──────────────────────────────────────────

const SKELETON: RegoCompletion[] = [
  {
    label:         'input',
    insertText:    'input',
    snippet:       false,
    flavour:       'variable',
    detail:        'the policy input document',
    documentation: 'Dot into it (`input.`) to see the fields this artefact’s policies receive.',
    group:         0,
  },
  {
    label:         'deny',
    insertText:    'deny contains msg if {\n\t${1:input.scan.findings.critical > 0}\n\tmsg := sprintf("${2:message}", [${3:}])\n}',
    snippet:       true,
    flavour:       'snippet',
    detail:        'deny rule',
    documentation: 'A deny rule. Zero messages = pass, one or more = fail.',
    group:         0,
  },
  {
    label:         'package',
    insertText:    'package terramantle.policy',
    snippet:       false,
    flavour:       'snippet',
    detail:        'required package header',
    documentation: 'Every Terramantle policy lives in this package.',
    group:         0,
  },
  {
    label:         'some',
    insertText:    'some ${1:finding} in ${2:input.findings}',
    snippet:       true,
    flavour:       'keyword',
    detail:        'iterate a collection',
    documentation: 'Bind a variable to each element in turn.',
    group:         0,
  },
]

const KEYWORDS: Array<{ label: string; insert?: string; detail: string; doc?: string }> = [
  { label: 'if',      detail: 'rule body' },
  { label: 'in',      detail: 'membership', doc: 'True when the value is in the collection: `x in {"a", "b"}`.' },
  { label: 'not',     detail: 'negation', doc: 'True when the expression is undefined or false.' },
  { label: 'every',   insert: 'every ${1:finding} in ${2:input.findings} {\n\t${3:condition}\n}', detail: 'universal quantifier', doc: 'True only when the body holds for EVERY element.' },
  { label: 'default', insert: 'default ${1:allow} := ${2:false}', detail: 'default value', doc: 'Value a rule takes when nothing else matches.' },
  { label: 'else',    insert: 'else := ${1:false}', detail: 'fallback branch' },
  { label: 'import',  insert: 'import rego.v1', detail: 'explicit Rego v1 opt-in', doc: 'Optional - the scanners run OPA 1.x, which is Rego v1 already.' },
  { label: 'with',    detail: 'value substitution', doc: 'Override part of the input for one expression (mostly used in tests).' },
]

/** Everything offered outside a member access: skeleton, keywords, then builtins. */
export const REGO_LANGUAGE_COMPLETIONS: RegoCompletion[] = [
  ...SKELETON,
  ...KEYWORDS.map(({ label, insert, detail, doc }): RegoCompletion => ({
    label,
    insertText: insert ?? label,
    snippet:    insert !== undefined,
    flavour:    'keyword',
    detail,
    ...(doc ? { documentation: doc } : {}),
    group:      1,
  })),
  // Namespaced builtins are offered by their full name here (`sem` -> `semver.compare`);
  // once the dot is typed, namespaceCompletions() takes over with member-only inserts.
  ...BUILTINS.map((b) => builtinCompletion(b, b.name)),
]

// ── Namespaced builtins (`regex.`, `time.`, `object.`, …) ─────────────────────

const NAMESPACE_BEFORE_DOT = /(?:^|[^A-Za-z0-9_.\]])([A-Za-z_][A-Za-z0-9_.]*)\.\w*$/

/** The dotted namespace the cursor sits under, e.g. `regex` in `regex.ma|`. */
export function namespaceTarget(lineUpToCursor: string): string | null {
  return NAMESPACE_BEFORE_DOT.exec(lineUpToCursor)?.[1] ?? null
}

/**
 * Builtins under a namespace. The inserted text is only the member, since the `regex.` the
 * author already typed sits outside the word Monaco replaces.
 */
export function namespaceCompletions(namespace: string): RegoCompletion[] {
  return BUILTINS
    .filter((b) => b.name.startsWith(`${namespace}.`))
    .map((b) => builtinCompletion(b, lastSegment(b.name)))
}
